// Umbraco Forms' Textarea field (.dacke-form-field__input, atoms/form-field.css) starts locked
// to a single compact row height to match Figma's Message field exactly (see that file's own
// comment on this - the design has no taller multi-line component to match). Growing it on input
// instead of leaving it fixed keeps that compact starting point while still letting a visitor see
// what they've typed across multiple lines, rather than internal-scrolling a single visible line.
//
// height:auto before reading scrollHeight is required every time, not just once - shrinking text
// (e.g. after deleting a line) would otherwise never reduce scrollHeight, since scrollHeight only
// grows to fit existing content and ignores a height that's already taller than needed.
//
// Setting height:auto on a <textarea> resolves scrollHeight against its rows HTML attribute, NOT
// against actual content - Umbraco Forms' own field-type template (FieldType.Textarea.cshtml)
// renders rows="4" by default (editable per-field in the Forms section as "Number of Rows"), so
// without this, the very first keystroke would measure scrollHeight at a 4-row baseline and jump
// straight there instead of growing line by line from one. Forcing rows="1" here first pins the
// auto-height baseline to a true single line regardless of that backoffice setting, matching the
// "always compact at rest" decision this field's CSS already makes.
const SELECTOR = "textarea.dacke-form-field__input";

function autogrow(textarea: HTMLTextAreaElement): void {
    textarea.rows = 1;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
}

function autogrowAll(): void {
    document.querySelectorAll<HTMLTextAreaElement>(SELECTOR).forEach(autogrow);
}

export function initFormTextareaAutogrow(): void {
    document.addEventListener("input", (event) => {
        const textarea = event.target as HTMLElement;
        if (textarea instanceof HTMLTextAreaElement && textarea.matches(SELECTOR)) {
            autogrow(textarea);
        }
    });

    // Text that's already there without an input event: the form re-rendered with the visitor's
    // text after a server validation error, autofill, or back/forward restoring it (pageshow).
    autogrowAll();
    window.addEventListener("pageshow", autogrowAll);
    // A reset empties the field after the event, so measure once it has.
    document.addEventListener("reset", () => setTimeout(autogrowAll, 0));

    // The same text wraps onto more or fewer lines when the field's width changes (rotating a
    // phone, resizing the window). Only width matters - autogrow itself changes the height.
    if ("ResizeObserver" in window) {
        const widths = new WeakMap<Element, number>();
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                if (widths.get(entry.target) !== width) {
                    widths.set(entry.target, width);
                    autogrow(entry.target as HTMLTextAreaElement);
                }
            }
        });
        document.querySelectorAll(SELECTOR).forEach((textarea) => observer.observe(textarea));
    }
}
